import type { SelectMultipleOnSelectAll } from '@/components_deprecated'
import {
  Checkbox,
  DateRangePicker,
  MonthPicker,
  Select,
  Toggle,
} from '@/components_deprecated'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { responsiveExportGridAreas } from '@/modules/companies/util'
import { useExportOptions } from '@/modules/exports/hooks'
import { useAbsenceReportForm } from '@/modules/timesheets/hooks'
import { handleDateRangeChange } from '@/utils/date/handleDateRangeChange'
import { ComponentSwitch, Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import styled from 'styled-components'
import type { FormButton } from '../common'
import { Field, Form, Section } from '../common'
import type { ExportCardProps } from '../types'

const SelectMultipleUsersWrapper = styled.div`
  position: relative;
  min-height: 54px;
`

/**
 * @deprecated This component is deprecated and will be removed in a future release. Absence exports now live in the absence-report view.
 */
export const ExportCompanyAbsencesCard: React.FC<ExportCardProps> = ({
  loading,
  onLoadingChange,
}) => {
  const translations = useTranslate({
    exportAll: 'settings.export-all-absences-for-period',
    period: 'common.period',
    exportType: 'common.type',
    exportOption: 'common.export-type',

    export: 'common.export',
    orderByUserType: 'common.sort-by-user-type',
    selectUsers: 'common.select-users',
    selectAll: 'common.select-all',
  })

  const {
    loading: formLoading,
    form,
    errors,
    submit,
    updateForm,
    handleExport,
    handler,
  } = useAbsenceReportForm(() => onLoadingChange(false))

  const exportOptions = useExportOptions()

  useEffect(() => {
    if (!formLoading || loading === formLoading) return
    onLoadingChange(formLoading)
  }, [formLoading, loading, onLoadingChange])

  const areas = !isMobile
    ? `
        ${responsiveExportGridAreas(true, false)}
        'sort sort sort'
      `
    : `
        ${responsiveExportGridAreas(true, false)}
        'sort'
      `

  const selectAllOnChange: SelectMultipleOnSelectAll = (
    options,
    setSelectAll
  ) => {
    return (value: boolean) => {
      if (value) {
        updateForm({ users: options.map(option => option.value) as string[] })
        setSelectAll(true)
      } else {
        updateForm({ users: [] })
        setSelectAll(false)
      }
    }
  }

  const formButtons = useMemo<FormButton[]>(
    () => [
      {
        text: (
          <>
            <Icon
              icon="download"
              type="light"
              color="primary"
              margin="0 0.5ch 0 0"
            />
            {translations.export}
          </>
        ),
        type: 'submit',
        buttonProps: {
          fullWidth: isMobile,
          disabled: loading,
        },
        onClick: submit(handleExport),
      },
    ],
    [handleExport, loading, submit, translations.export]
  )

  return (
    <Section
      header={translations.exportAll}
      loading={formLoading}
      actions={
        <Toggle
          value={form.useDateRange}
          thumbBackground="secondary"
          iconMode="svg"
          localOnIconProps={{
            icon: 'calendar-selection',
            fill: form.useDateRange ? 'white' : 'primary',
          }}
          localOffIconProps={{
            icon: 'calendar-month',
            fill: !form.useDateRange ? 'white' : 'primary',
          }}
          onChange={() => handler('useDateRange')(!form.useDateRange)}
        />
      }
    >
      <Form
        grid={{
          columns: !isMobile ? '3fr 2fr' : '1fr',
          areas: areas,
        }}
        buttons={formButtons}
      >
        <Field area="month">
          <label>{translations.period}</label>
          <ComponentSwitch>
            <ComponentSwitch.Case case={form.useDateRange}>
              <DateRangePicker
                value={[form.dateStart, form.dateEnd]}
                grayed={false}
                onChange={handleDateRangeChange(updateForm)}
              />
            </ComponentSwitch.Case>
            <ComponentSwitch.Case default>
              <MonthPicker
                value={form.month}
                light
                futureYears={0}
                noYearCycle
                onChange={handler('month')}
              />
            </ComponentSwitch.Case>
          </ComponentSwitch>
        </Field>

        <Field area="exportOption">
          <label>{translations.exportOption}</label>
          <Select
            value={form.exportOption}
            options={exportOptions}
            fullWidth
            height="3.875rem"
            onChange={handler('exportOption')}
          />
        </Field>

        <Field area="user">
          <label>{translations.selectUsers}</label>
          <SelectMultipleUsersWrapper>
            <SelectMultipleUsers
              selected={form.users ?? []}
              dropdownPageName={DropdownNames.ABSENCES}
              grayed={false}
              width="100%"
              minWidth={isMobile ? '0' : '260px'}
              onChange={handler('users')}
              onSelectAll={selectAllOnChange}
              withSelectAll={true}
              error={errors.users}
            />
          </SelectMultipleUsersWrapper>
        </Field>

        <Field area="sort">
          <label>{translations.orderByUserType}</label>
          <div>
            <Checkbox
              checked={form.orderByUserType ?? false}
              onChange={handler('orderByUserType')}
              label={translations.orderByUserType}
              inline
            />
          </div>
        </Field>
      </Form>
    </Section>
  )
}
